/**
 * Dust motes: ≤ 1500 points advected on the CPU by an analytic curl field (the curl of a sum of drifting
 * sines is exact and cheap), gently orbiting the mandala, drawn additively as GL points from one buffer
 * updated with a single bufferSubData per frame.
 */
import { createProgram, type Program } from './gl';
import type { RGB } from './palette';
import { PARTICLE_FRAG, PARTICLE_VERT } from './shaders/particles';

const STRIDE = 4; // x, y, size, alpha
const LOBES: ReadonlyArray<readonly [number, number, number, number, number]> = [
  // amplitude, kx, ky, omega, phase  (in normalised coords: p / R)
  [0.9, 1.7, 1.1, 0.13, 0.0],
  [0.6, 2.9, 2.3, -0.21, 1.7],
  [0.35, 4.7, 3.7, 0.31, 3.1],
];

/** Curl of psi(x, y, t) = Σ A sin(kx·x + ky·y + ω t + φ): v = (∂ψ/∂y, −∂ψ/∂x). Pure, testable. */
export function curlVelocity(x: number, y: number, t: number, out: [number, number]): [number, number] {
  let vx = 0, vy = 0;
  for (const [A, kx, ky, om, ph] of LOBES) {
    const c = Math.cos(kx * x + ky * y + om * t + ph) * A;
    vx += ky * c;
    vy -= kx * c;
  }
  out[0] = vx; out[1] = vy;
  return out;
}

export class Particles {
  readonly count: number;
  private readonly gl: WebGL2RenderingContext;
  private readonly prog: Program;
  private readonly vao: WebGLVertexArrayObject;
  private readonly vbo: WebGLBuffer;
  private readonly data: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly seed: Float32Array;
  private readonly tmp: [number, number] = [0, 0];
  private rng = 1234567;

  constructor(gl: WebGL2RenderingContext, count: number) {
    this.gl = gl;
    this.count = count;
    this.prog = createProgram(gl, PARTICLE_VERT, PARTICLE_FRAG);
    this.data = new Float32Array(count * STRIDE);
    this.life = new Float32Array(count);
    this.maxLife = new Float32Array(count);
    this.seed = new Float32Array(count);
    const vao = gl.createVertexArray();
    const vbo = gl.createBuffer();
    if (!vao || !vbo) throw new Error('particle buffers failed');
    this.vao = vao; this.vbo = vbo;
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, this.data.byteLength, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 4, gl.FLOAT, false, STRIDE * 4, 0);
    gl.bindVertexArray(null);
    for (let i = 0; i < count; i++) this.life[i] = -1; // spawn lazily on first update
  }

  private rand(): number {
    // xorshift32: deterministic, allocation-free
    let x = this.rng;
    x ^= x << 13; x ^= x >>> 17; x ^= x << 5;
    this.rng = x >>> 0;
    return (this.rng & 0xffffff) / 0x1000000;
  }

  private spawn(i: number, cx: number, cy: number, R: number, initial: boolean): void {
    // bias toward the rings: radius from a triangular distribution around 0.75 R, up to 1.6 R
    const u = this.rand(), v = this.rand();
    const rad = R * (0.15 + 1.45 * Math.abs(u - v) + 0.0 * u);
    const ang = this.rand() * Math.PI * 2;
    const o = i * STRIDE;
    this.data[o] = cx + Math.cos(ang) * rad;
    this.data[o + 1] = cy + Math.sin(ang) * rad;
    this.data[o + 2] = 1.2 + this.rand() * 2.2;
    this.data[o + 3] = 0;
    this.maxLife[i] = 6 + this.rand() * 8;
    this.life[i] = initial ? this.rand() * this.maxLife[i]! : 0;
    this.seed[i] = this.rand();
  }

  /** Advance and upload. `energy` (0..1) from the number of sounding voices makes the dust livelier. */
  update(dtSec: number, cx: number, cy: number, R: number, cssW: number, cssH: number, timeSec: number, energy: number, calm: boolean): void {
    const dt = Math.min(0.05, Math.max(0, dtSec));
    const speed = (calm ? 6 : 14 + 18 * energy) * (R / 400);
    for (let i = 0; i < this.count; i++) {
      if (this.life[i]! < 0) this.spawn(i, cx, cy, R, true);
      const o = i * STRIDE;
      let x = this.data[o]!, y = this.data[o + 1]!;
      const nx = (x - cx) / R, ny = (y - cy) / R;
      const v = curlVelocity(nx, ny, timeSec, this.tmp);
      // slow orbit around the bindu plus the curl field
      const orb = 0.35 * (0.5 + this.seed[i]!);
      x += (v[0] * 0.5 + -ny * orb) * speed * dt;
      y += (v[1] * 0.5 + nx * orb) * speed * dt;
      const life = this.life[i]! + dt;
      const ml = this.maxLife[i]!;
      const fade = Math.min(1, life / 1.5, (ml - life) / 2);
      const out = x < -20 || y < -20 || x > cssW + 20 || y > cssH + 20;
      if (life >= ml || out) { this.spawn(i, cx, cy, R, false); continue; }
      this.life[i] = life;
      this.data[o] = x; this.data[o + 1] = y;
      this.data[o + 3] = (0.18 + 0.32 * energy) * Math.max(0, fade) * (0.6 + 0.4 * Math.sin(timeSec * (1 + this.seed[i]! * 2) + this.seed[i]! * 20));
    }
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.data);
  }

  draw(cssW: number, cssH: number, dpr: number, colour: RGB): void {
    const gl = this.gl;
    this.prog.use();
    gl.uniform2f(this.prog.loc('uCanvasCss'), cssW, cssH);
    gl.uniform1f(this.prog.loc('uDpr'), dpr);
    gl.uniform3f(this.prog.loc('uColor'), colour[0], colour[1], colour[2]);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.POINTS, 0, this.count);
    gl.bindVertexArray(null);
    gl.disable(gl.BLEND);
  }

  destroy(): void {
    this.gl.deleteBuffer(this.vbo);
    this.gl.deleteVertexArray(this.vao);
    this.prog.destroy();
  }
}
